import React from 'react';
import { gql } from '@apollo/client';
import {
  NumberField,
  FunctionField,
  TopToolbar,
  EditButton,
  Show,
  TabbedShowLayout,
  Tab,
  TextField,
  BooleanField,
  TabbedShowLayoutTabs,
  useRecordContext,
  useGetRecordId,
  useGetOne,
} from 'react-admin';
import { PermissionGroupAccountTypes } from 'typings/enums';
import getRelationshipsTab from 'components/tabs/RelationshipsTab';
import MoneyField from 'components/MoneyField';
import DateTimeField from 'components/DateTimeField';
import PayPositionRateSettingsTab from 'components/tabs/PayPositionRateSettingsTab';
import ChargePositionRateSettingsTab from 'components/tabs/ChargePositionRateSettingsTab';
import RefundFormButton from 'resources/refunds/RefundForm/RefundFormButton';
import LocationAside from './LocationAside';
import PositionsTab from './tabs/PositionsTab';
import RateSettingsTab from 'components/tabs/RateSettingsTab';
import TimestampFields from 'components/TimestampFields';
import MetabaseUrlField from 'components/MetabaseUrlField';
import LinkField from 'components/LinkField';
import { Link } from 'react-router-dom';
import EnumField from 'components/Enum/EnumField';
import useCheckAccess from 'hooks/useCheckAccess';
import BillingGroupsTab from './tabs/BillingGroupsTab';
import JobsTab from './tabs/JobsTab';
import AccountsTab from './tabs/AccountsTab';
import AssignmentsTab from './tabs/AssignmentsTab';
import NotesTab from 'components/tabs/NotesTab';
import RequirementsTab from './tabs/RequirementsTab';
import DisputesTab from 'components/tabs/DisputesTab';
import AgenciesTab from './tabs/AgenciesTab';
import MigrateLocationButton from './migrate_location/MigrateLocationButton';
import OffboardLocationButton from './location_offboard/OffboardLocationButton';
import CustomRates from './CustomRates';
import { CoordinatesField } from 'components/CoordinatesField';
import ChangeRateStructureButton from './location_change_rate_structure/ChangeRateStructureButton';
import { RateSettingsDisplayHelper } from 'utils/displayHelpers/rate-settings';
import useGate from 'hooks/useGate';
import { gateNames } from 'constants/statsig';
import { shouldShowAcknowledgementStatus } from 'utils/acknowledgementHelpers';
import { IRMProfessionalsTab } from 'components/tabs/IRMProfessionalsTab';

const RelationshipsTab = getRelationshipsTab('Location', 'Professional');
const SNF_LOCATION_ID = 7;

const LocationActions = () => {
  const record = useRecordContext();
  const couldBeRefunded = record?.balance_cents > 0;
  const isLocationSnf = record?.location_type_id === SNF_LOCATION_ID;
  const hasLprs = !!record?.location_position_rate_settings?.length;
  const showCreateCustomLprs = isLocationSnf && !hasLprs;
  const showChangeRateStructureButton = shouldShowAcknowledgementStatus(record);

  return (
    <TopToolbar>
      {showChangeRateStructureButton && <ChangeRateStructureButton />}
      {showCreateCustomLprs && <CustomRates />}
      {couldBeRefunded && <RefundFormButton billingGroupId={record.id as number} mr={2} />}
      {record?.status === 'active' && <OffboardLocationButton />}
      <MigrateLocationButton />
      <EditButton />
    </TopToolbar>
  );
};

const readableLocationSize = {
  size_1_9: '1-9',
  size_10_49: '10-49',
  size_50_100: '50-100',
  size_100_plus: '100+',
};

const readableCoverageNeeds = {
  primary_care: 'Consistent Need',
  specialty_care: 'Surge in work',
  emergency: 'Cover for PTO or call-out',
  other: 'Temp-to-Hire or sourcing full-time hires',
};

const readableNeedRegularity = {
  almost_daily: 'Almost Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  few_times_quarter: 'A few times a quarter',
};

const readableNeedSize = {
  workers_0_5: '0-5 workers',
  workers_6_10: '6-10 workers',
  workers_11_20: '11-20 workers',
  workers_20_plus: '20 or more workers',
};

const LocationsShow = () => {
  const id = useGetRecordId();
  const { data: record } = useGetOne('Location', { id });
  const checkAccess = useCheckAccess();
  const { value: isRateSettingsAcknowledgementEnabled } = useGate(
    gateNames.facilityRateAcknowledgement,
  );

  const isSaas =
    Boolean(record?.company?.saas_credentialing_enabled) ||
    Boolean(record?.company?.saas_scheduling_enabled);

  return (
    <Show actions={<LocationActions />} aside={<LocationAside />} data-testid="locations-show">
      <TabbedShowLayout tabs={<TabbedShowLayoutTabs variant="scrollable" />}>
        <Tab label="info">
          <TextField label="ID" source="id" />
          <LinkField label="Company" source="company_id" />
          <TextField label="Name" source="name" />
          <TextField label="Unit" source="unit" />
          <TextField label="Location Type" source="location_type.label" />
          <TextField label="Status" source="status" />
          <TextField label="Offboard Reason" source="offboard_reason" />
          <TextField label="Phone Number" source="phone_number" />
          <TextField label="About" source="about" />
          <NumberField source="mileage_requirement" />
          <NumberField label="Minimum Years Of Experience" source="min_years_of_experience" />
          <CoordinatesField label="Coordinates (Lat, Long)" showMapLink={true} />
          <TextField label="Address First" source="address_first" />
          <TextField label="Address Second" source="address_second" />
          <TextField label="City" source="address_city" />
          <TextField label="Zip" source="address_zip" />
          <TextField label="State" source="state.name" />
          <TextField label="Market" source="market.display_name" />
          {isRateSettingsAcknowledgementEnabled && (
            <FunctionField
              label="Acknowledged Rate Structure"
              render={({ location_rate_settings, primary_billing_group }) => {
                if (!location_rate_settings?.id) {
                  return 'No rate settings configured';
                }

                const { settings } = location_rate_settings;

                const rateSettingsDisplayHelper = new RateSettingsDisplayHelper({
                  settings,
                  location: { primary_billing_group },
                });
                const activeRate = rateSettingsDisplayHelper.getActiveRate();

                if (!activeRate) {
                  return 'Not set';
                }

                return activeRate === 'blended-rate' ? '12h rate' : '8h rate';
              }}
            />
          )}
          <FunctionField
            label="Location size"
            render={(record) => readableLocationSize[record.location_size] || record.location_size}
          />
          <FunctionField
            label="Coverage needs"
            render={(record) =>
              record.coverage_needs
                ?.map((need) => readableCoverageNeeds[need] || need)
                .join(', ') || 'None'
            }
          />
          <FunctionField
            label="Need regularity"
            render={(record) =>
              readableNeedRegularity[record.need_regularity] || record.need_regularity
            }
          />
          <FunctionField
            label="Need size"
            render={(record) => readableNeedSize[record.need_size] || record.need_size}
          />
          <NumberField
            label="Non-Billable Orientation Burden Hours"
            source="non_billable_orientation_burden_hours"
          />
          <TextField label="Parking Instructions" source="parking_instructions" />
          <BooleanField label="Parking Free" source="parking_free" />
          <BooleanField label="Parking Reimbursed" source="parking_reimbursed" />
          <BooleanField label="Parking Validated" source="parking_validated" />
          <MoneyField label="Parking Cost" source="parking_cost_cents" />
          <MoneyField label="Parking Reimbursement" source="parking_reimbursement_cents" />
          <FunctionField
            label="Account Manager"
            render={(r) =>
              r.account_manager &&
              r.account_manager.account && (
                <Link to={`/Administrator/${r.account_manager_id}/show`}>
                  {r.account_manager.account.name}
                </Link>
              )
            }
          />
          <FunctionField
            label="Clinical Account Manager"
            render={(r) =>
              r.clinical_account_manager &&
              r.clinical_account_manager.account && (
                <Link to={`/Administrator/${r.clinical_account_manager_id}/show`}>
                  {r.clinical_account_manager.account.name}
                </Link>
              )
            }
          />
          <FunctionField
            label="Sales Associate"
            render={(r) =>
              r.sales_associate &&
              r.sales_associate.account && (
                <Link to={`/Administrator/${r.sales_associate_id}/show`}>
                  {r.sales_associate.account.name}
                </Link>
              )
            }
          />
          <DateTimeField
            format="MM/dd/yyyy HH:mm"
            tzSource="timezone_lookup"
            label="Last Time Job Posted"
            source="last_time_job_posted"
          />
          <TextField label="Detailed Notes" source="detailed_notes" />
          <BooleanField label="Scrubs Provided" source="scrubs_provided" />
          <BooleanField label="Drug Screening Required" source="drug_screening_required" />
          <EnumField emptyText="none" label="Covid Requirement" source="covid_requirement" />
          <TextField label="Timezone Lookup" source="timezone_lookup" />
          <BooleanField label="Allow Per Diem Jobs" source="allow_per_diem_jobs" />
          <BooleanField label="Allow Assignments" source="allow_assignments" />
          {record?.company?.saas_credentialing_enabled && (
            <BooleanField label="SaaS Credentialing Enabled" source="saas_credentialing_enabled" />
          )}
          {record?.company?.saas_scheduling_enabled && (
            <BooleanField label="SaaS Scheduling Enabled" source="saas_scheduling_enabled" />
          )}
          <NumberField
            label="Charge overtime multiplier"
            source="main_rate_setting.charge_overtime_multiplier"
          />
          <TextField
            label="Charge overtime frequency"
            source="main_rate_setting.charge_overtime_frequency"
          />
          <NumberField label="Cancellation Fee Window" source="cancellation_fee_window" />
          <NumberField
            label="Cancellation Fee Charge Hours"
            source="cancellation_fee_charge_hours"
          />
          <NumberField label="Guaranteed Minimum Hours" source="guaranteed_minimum_hours" />
          <NumberField
            label="Start Clock In Early Minutes"
            source="start_clock_in_early_window_minutes"
            defaultValue={0}
          />
          <NumberField
            label="Clock In Early Minutes"
            source="clock_in_early_window"
            defaultValue={0}
          />
          <NumberField
            label="Maximum Clock Out Period"
            source="maximum_late_clock_out_grace_period_minutes"
            defaultValue={0}
          />
          <NumberField
            label="Approval Free Grace Period"
            source="approval_free_late_clock_out_grace_period_minutes"
          />
          <NumberField label="Common Shift Length Hours" source="common_shift_length_hours" />
          <BooleanField
            label="Allow pro to adjust start/end times"
            source="professional_can_adjust_times"
          />
          <BooleanField source="enable_geo_fence" />
          <NumberField source="geo_fence_radius" defaultValue={0} />
          <BooleanField source="location_confirmation_job_irregularities_enabled" />
          {checkAccess('view', 'Audits') && <MetabaseUrlField />}
          <BooleanField label="Require 530 on Clock Out" source="require_upload_on_clock_out" />
          <BooleanField
            label="Require photo on clock in"
            source="require_facility_timecard_system_photo_on_clock_in"
          />
          <BooleanField
            label="Require photo on clock out"
            source="require_facility_timecard_system_photo_on_clock_out"
          />
          <BooleanField source="ignore_signature_when_creating_meal_break" />
          <BooleanField source="allow_weekly_overtime_settings" />
          <EnumField source="professional_weekly_overtime_settings" />
          <TextField label="External ID" source="external_id" />
          <BooleanField source="pro_pool_enabled" />
          <BooleanField source="permanent_placement_enabled" />
          <BooleanField label="Shift Confirmation Required" source="require_shift_confirmation" />
          {checkAccess('view', 'Location', 'facility_managed_agency_enabled') && (
            <BooleanField source="facility_managed_agency_enabled" />
          )}
          {checkAccess('view', 'Location', 'late_clock_in_irregularities_enabled') && (
            <BooleanField source="late_clock_in_irregularities_enabled" />
          )}
          {checkAccess('view', 'Location', 'missed_clock_out_irregularities_enabled') && (
            <BooleanField source="missed_clock_out_irregularities_enabled" />
          )}
          <BooleanField label="SaaS Bypass Requirements" source="saas_bypass_requirements" />
          <TimestampFields />
        </Tab>

        <Tab label="Accounts" path="accounts">
          <AccountsTab
            filter={{
              search: {
                account_types: [
                  PermissionGroupAccountTypes.administrator,
                  PermissionGroupAccountTypes.agency,
                  PermissionGroupAccountTypes.facility,
                ],
              },
            }}
          />
        </Tab>

        {isSaas && (
          <Tab label="Staff" path="staff">
            <IRMProfessionalsTab />
          </Tab>
        )}

        {checkAccess('view', 'BillingGroup') && (
          <Tab label="Billing Groups" path="billing-groups">
            <BillingGroupsTab />
          </Tab>
        )}
        {checkAccess('view', 'AgencyLocation') && (
          <Tab label="Agencies" path="agency-locations">
            <AgenciesTab />
          </Tab>
        )}
        {checkAccess('view', 'Job') && (
          <Tab label="Jobs" path="jobs">
            <JobsTab />
          </Tab>
        )}
        {checkAccess('view', 'Assignment') && (
          <Tab label="Assignments" path="assignments">
            <AssignmentsTab />
          </Tab>
        )}
        {checkAccess('view', 'Position') && (
          <Tab label="positions" path="location_positions">
            <PositionsTab />
          </Tab>
        )}
        {checkAccess('view', 'Relationship') && (
          <Tab label="Relationships" path="relationships">
            <RelationshipsTab
              canCreate={checkAccess('create', 'Relationship')}
              canDelete={checkAccess('delete', 'Relationship')}
            />
          </Tab>
        )}
        {checkAccess('view', 'LocationRateSetting') && (
          <Tab label="Rate settings" path="rate-settings">
            <RateSettingsTab />
          </Tab>
        )}
        {checkAccess('view', 'LocationPositionRateSetting') && (
          <Tab label="Pay Position Rate settings" path="pay-position-rate-settings">
            <PayPositionRateSettingsTab />
          </Tab>
        )}
        {checkAccess('view', 'LocationPositionRateSetting') && (
          <Tab label="Charge Position Rate settings" path="charge-position-rate-settings">
            <ChargePositionRateSettingsTab />
          </Tab>
        )}
        {checkAccess('view', 'Note') && (
          <Tab label="Notes" path="notes">
            <NotesTab />
          </Tab>
        )}
        {checkAccess('view', 'Dispute') && (
          <Tab label="Disputes" path="disputes">
            <DisputesTab target="location_id" />
          </Tab>
        )}
        <Tab label="Requirements" path="requirements">
          <RequirementsTab
            locationRequirementsFilter={{ cohort_eligibility: ['medely_internal', 'both'] }}
          />
        </Tab>
        <Tab label="SaaS Requirements" path="saas-requirements">
          <RequirementsTab
            locationRequirementsLabel="SaaS Location Requirements"
            locationRequirementsFilter={{ cohort_eligibility: ['saas', 'both'] }}
            showCompanyRequirements={false}
            showStateRequirements={false}
          />
        </Tab>
      </TabbedShowLayout>
    </Show>
  );
};
LocationsShow.query = gql`
  query location($id: Int!) {
    location(id: $id) {
      id
      company_id
      company {
        saas_scheduling_enabled
        saas_credentialing_enabled
      }
      name
      location_type {
        id
        label
      }
      status
      approval_free_late_clock_out_grace_period_minutes
      phone_number
      about
      mileage_requirement
      min_years_of_experience
      coordinates {
        latitude
        longitude
      }
      address_first
      address_second
      address_city
      address_zip
      state {
        id
        name
      }
      market {
        id
        display_name
      }
      billing_groups {
        id
        plan
      }
      non_billable_orientation_burden_hours
      parking_instructions
      parking_free
      parking_reimbursed
      parking_validated
      parking_cost_cents
      parking_reimbursement_cents
      primary_billing_group {
        plan
      }
      account_manager_id
      account_manager {
        id
        account {
          id
          name
          first_name
          last_name
        }
      }
      clinical_account_manager_id
      clinical_account_manager {
        id
        account {
          id
          name
          first_name
          last_name
        }
      }
      sales_associate_id
      sales_associate {
        id
        account {
          id
          name
          first_name
          last_name
        }
      }
      last_time_job_posted
      location_position_rate_settings {
        id
      }
      location_rate_settings {
        id
        settings
      }
      positions {
        id
        display_name
      }
      timezone_lookup
      detailed_notes
      scrubs_provided
      drug_screening_required
      covid_requirement
      allow_per_diem_jobs
      allow_assignments
      cancellation_fee_window
      cancellation_fee_charge_hours
      guaranteed_minimum_hours
      start_clock_in_early_window_minutes
      clock_in_early_window
      maximum_late_clock_out_grace_period_minutes
      professional_can_adjust_times
      enable_geo_fence
      geo_fence_radius
      require_upload_on_clock_out
      require_facility_timecard_system_photo_on_clock_in
      require_facility_timecard_system_photo_on_clock_out
      ignore_signature_when_creating_meal_break
      created_at
      updated_at
      allow_reserved_for_favorites
      allow_weekly_overtime_settings
      professional_weekly_overtime_settings
      external_id
      unit
      common_shift_length_hours
      location_confirmation_job_irregularities_enabled
      pro_pool_enabled
      permanent_placement_enabled
      require_shift_confirmation
      facility_managed_agency_enabled
      offboard_reason
      location_size
      coverage_needs
      need_regularity
      need_size
      late_clock_in_irregularities_enabled
      missed_clock_out_irregularities_enabled
      saas_credentialing_enabled
      saas_scheduling_enabled
      saas_bypass_requirements
    }
  }
`;

export default LocationsShow;
